import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Save, X, Briefcase, Star, Tag } from "lucide-react";
import IconPicker from "@/components/IconPicker";
import AdminLayout from "@/components/AdminLayout";

const ICON_OPTIONS = [
  "Scissors", "Brush", "Pin", "Sparkles", "Car", "Heart", "Flame",
  "Star", "Stethoscope", "Wrench", "Briefcase", "PawPrint", "Camera",
];

export default function IndustriesManager() {
  const [industries, setIndustries] = useState([]);
  const [termKeys, setTermKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [migration, setMigration] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [i, k] = await Promise.all([
        api.get("/admin/industries"),
        api.get("/admin/terminology-keys"),
      ]);
      setIndustries(i.data);
      setTermKeys(k.data.map((x) => x.key));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    slug: "", name: "", description: "", icon: "Sparkles",
    sort_order: industries.length + 1, active: true, is_default: false,
    terminology: {},
  });

  const startEdit = (i) => setEditing({
    ...i,
    terminology: { ...(i.terminology || {}) },
  });

  const setTerm = (key, value) => {
    if (!editing) return;
    const next = { ...editing.terminology };
    if (value === "" || value == null) delete next[key];
    else next[key] = value;
    setEditing({ ...editing, terminology: next });
  };

  const save = async () => {
    if (!editing) return;
    setErr("");
    setSaving(true);
    try {
      const payload = {
        slug: editing.slug.trim().toLowerCase(),
        name: editing.name.trim(),
        description: editing.description || "",
        icon: editing.icon || "Sparkles",
        sort_order: parseInt(editing.sort_order) || 0,
        active: !!editing.active,
        is_default: !!editing.is_default,
        terminology: editing.terminology || {},
      };
      if (editing.id) {
        await api.put(`/admin/industries/${editing.id}`, payload);
      } else {
        await api.post("/admin/industries", payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : (detail?.message || "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (i) => {
    if (!window.confirm(`Berufsgruppe „${i.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/industries/${i.id}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "industry_in_use") {
        const targets = industries.filter((q) => q.active && q.id !== i.id);
        if (targets.length === 0) {
          alert("Keine andere aktive Berufsgruppe vorhanden — bitte zuerst eine aktivieren.");
          return;
        }
        setMigration({ industry: i, in_use: detail.in_use, target_id: targets[0].id });
      } else {
        alert((typeof detail === "string" ? detail : detail?.message) || "Löschen fehlgeschlagen");
      }
    }
  };

  const confirmMigrateAndDelete = async () => {
    if (!migration?.target_id) return;
    setMigrating(true);
    try {
      await api.delete(`/admin/industries/${migration.industry.id}`, {
        params: { reassign_to: migration.target_id },
      });
      setMigration(null);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert((typeof detail === "string" ? detail : detail?.message) || "Umbuchen / Löschen fehlgeschlagen");
    } finally {
      setMigrating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-12" data-testid="industries-manager">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="mv-label mb-2">Plattform-Konfiguration</p>
            <h1 className="font-display text-4xl">Berufsgruppen</h1>
            <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>
              {industries.length} Berufsgruppen · {termKeys.length} überschreibbare Begriffe
            </p>
          </div>
          <button data-testid="new-industry-btn" onClick={startNew} className="mv-btn">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Neue Berufsgruppe
          </button>
        </div>

        {loading ? (
          <p style={{ color: "var(--mv-text-muted)" }}>Lade...</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((i) => (
              <div
                key={i.id}
                data-testid={`industry-card-${i.slug}`}
                className="mv-card p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => startEdit(i)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
                      <h3 className="font-display text-xl">{i.name}</h3>
                      {i.is_default && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1" style={{ background: "var(--mv-primary)", color: "#fff" }}>
                          <Star className="w-2.5 h-2.5" strokeWidth={2} /> Standard
                        </span>
                      )}
                      {!i.active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: "#e7ebee", color: "#5b6770" }}>
                          Inaktiv
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-mono" style={{ color: "var(--mv-text-muted)" }}>{i.slug}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(i); }}
                    data-testid={`delete-industry-${i.slug}`}
                    className="p-1.5 rounded hover:bg-[var(--mv-surface-2)]"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
                  </button>
                </div>
                {i.description && <p className="text-sm mb-3" style={{ color: "var(--mv-text-muted)" }}>{i.description}</p>}
                <div className="flex items-center gap-3 text-xs" style={{ color: "var(--mv-text-muted)" }}>
                  <span className="inline-flex items-center gap-1">
                    <Tag className="w-3 h-3" strokeWidth={1.5} />
                    {Object.keys(i.terminology || {}).length} Begriffe überschrieben
                  </span>
                  <span>·</span>
                  <span>{i.in_use} Salon{i.in_use === 1 ? "" : "s"}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="industry-edit-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="mv-label mb-1">Berufsgruppe bearbeiten</p>
                  <h2 className="font-display text-2xl">{editing.id ? editing.name : "Neue Berufsgruppe"}</h2>
                </div>
                <button onClick={() => setEditing(null)} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]">
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>

              {err && (
                <p data-testid="industry-edit-error" className="text-sm px-3 py-2 rounded mb-3 bg-red-50 text-red-700">{err}</p>
              )}

              <div className="grid md:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs mb-1">Name</label>
                  <input data-testid="industry-name-input" className="mv-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs mb-1">Slug (intern)</label>
                  <input data-testid="industry-slug-input" className="mv-input font-mono" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} disabled={!!editing.id} />
                </div>
                <div>
                  <label className="block text-xs mb-1">Icon</label>
                  <IconPicker
                    value={editing.icon || "Sparkles"}
                    onChange={(v) => setEditing({ ...editing, icon: v || "Sparkles" })}
                    testId="industry-icon-picker"
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">Sortierung</label>
                  <input type="number" className="mv-input" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs mb-1">Beschreibung</label>
                <textarea data-testid="industry-desc-input" className="mv-input min-h-[60px]" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>

              <div className="flex gap-4 mb-5">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="industry-active-toggle" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                  Aktiv
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid="industry-default-toggle" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} />
                  Als Standard markieren
                </label>
              </div>

              <div className="mb-5">
                <p className="mv-label mb-2">Begriffs-Mapping</p>
                <p className="text-xs mb-4" style={{ color: "var(--mv-text-muted)" }}>
                  Lass ein Feld leer, um den Standard-Begriff zu verwenden. {Object.keys(editing.terminology || {}).length} von {termKeys.length} Begriffen überschrieben.
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {termKeys.map((k) => (
                    <div key={k}>
                      <label className="block text-[11px] mb-1 font-mono" style={{ color: "var(--mv-text-muted)" }}>{k}</label>
                      <input
                        data-testid={`term-input-${k}`}
                        className="mv-input text-sm"
                        value={editing.terminology?.[k] || ""}
                        onChange={(e) => setTerm(k, e.target.value)}
                        placeholder="(Standard)"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end sticky bottom-0 bg-white pt-3 border-t" style={{ borderColor: "var(--mv-border)" }}>
                <button onClick={() => setEditing(null)} className="mv-btn-ghost" disabled={saving}>Abbrechen</button>
                <button data-testid="industry-save-btn" onClick={save} className="mv-btn" disabled={saving || !editing.name || !editing.slug}>
                  <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Speichert…" : "Speichern"}
                </button>
              </div>
            </div>
          </div>
        )}

        {migration && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-md" data-testid="industry-migrate-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="mv-label mb-1">Salons umbuchen</p>
                  <h2 className="font-display text-2xl">„{migration.industry.name}&ldquo; löschen</h2>
                </div>
                <button onClick={() => setMigration(null)} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]" disabled={migrating}>
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <p className="text-sm mb-4" style={{ color: "var(--mv-text-muted)" }}>
                <strong>{migration.in_use}</strong> Salon(s) nutzen diese Berufsgruppe. Wähle eine Zielgruppe, auf die alle Salons automatisch umgebucht werden.
              </p>
              <div className="mb-5">
                <label className="block text-xs mb-1">Zielgruppe</label>
                <select
                  data-testid="industry-migrate-target"
                  className="mv-input"
                  value={migration.target_id}
                  onChange={(e) => setMigration({ ...migration, target_id: e.target.value })}
                  disabled={migrating}
                >
                  {industries.filter((q) => q.active && q.id !== migration.industry.id).map((q) => (
                    <option key={q.id} value={q.id}>{q.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setMigration(null)} className="mv-btn-ghost" disabled={migrating}>Abbrechen</button>
                <button
                  data-testid="industry-migrate-confirm-btn"
                  onClick={confirmMigrateAndDelete}
                  className="mv-btn"
                  disabled={migrating || !migration.target_id}
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {migrating ? "Wird verarbeitet…" : "Umbuchen & löschen"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
